import React, { useEffect, useState } from "react";
import {
    Card, CardBody, Row, Col, Table, Badge, Input
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import { toast } from "react-toastify";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatIST } from "../../Helpers/Utils";

const StockHistory = () => {

    const dispatch = useDispatch();
    const user = useSelector(s => s.customer.customer);

    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [total, setTotal] = useState(0)
    const [summary, setSummary] = useState(null);

    /* PRODUCT LIST */
    const [products, setProducts] = useState([]);
    const [product, setProduct] = useState(null);

    /* FILTERS */
    const [variantId, setVariantId] = useState("");
    const [type, setType] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const typeOptions = [
        { value: "in", label: "Stock IN" },
        { value: "out", label: "Stock OUT" }
    ];

    /* LOAD PRODUCTS */
    const loadProducts = async () => {
        try {
            const res = await Handler({
                method: "get",
                url: `/products/search.php?outlet_id=${user?.outlet_id}`
            });
            if (res?.success) {
                setProducts(
                    (res.data || []).map(p => ({
                        value: p.product_id,
                        label: p.name
                    }))
                );
            }
        } catch { }
    };

    /* LOAD HISTORY */
    const load = async () => {
        dispatch(loadingTrue());
        try {
            const params = new URLSearchParams({ outlet_id: user?.outlet_id, page, limit, summary: "yes" });
            if (product) params.append("product_id", product.value);
            if (variantId) params.append("variant_id", variantId);
            if (type) params.append("io", type.value);
            if (fromDate) params.append("from_date", fromDate.toISOString().split("T")[0]);
            if (toDate) params.append("to_date", toDate.toISOString().split("T")[0]);
            const res = await Handler({ method: "get", url: `/inventory/history.php?${params.toString()}` });
            if (res?.success) {
                setRows(res.data.records || []);
                setSummary(res.data.summary || null);
                setTotal(res.data.total || 0);
            } else {
                toast.error(res?.message || "Failed to load");
            }
        } catch {
            toast.error("Network error");
        }
        dispatch(loadingFalse());
    };


    /* INITIAL LOAD */
    useEffect(() => {
        loadProducts();
    }, []);

    /* PAGE CHANGE → LOAD */
    useEffect(() => {
        load();
    }, [page]);

    /* FILTER CHANGE → RESET PAGE (AUTO LOAD) */
    useEffect(() => {
        setPage(1);
        load();
    }, [product, variantId, type, fromDate, toDate]);

    /* QTY BADGE */
    const qtyBadge = (q) =>
        q > 0
            ? <Badge color="danger">+{q}</Badge>
            : <Badge color="success">{q}</Badge>;

    return (
        <>
            {/* HEADER */}
            <Row className="mb-3">
                <Col>
                    <h5 className="fw-bold">Stock History</h5>
                    <small className="text-muted">Inventory movement logs</small>
                </Col>
            </Row>

            {/* FILTERS */}
            <Card className="mb-3 filter-card">
                <CardBody>
                    <Row className="g-3 align-items-end">

                        <Col md={3}>
                            <label className="small">Product</label>
                            <Select
                                options={products}
                                value={product}
                                onChange={setProduct}
                                placeholder="Select product"
                                isClearable
                            />
                        </Col>

                        <Col md={2}>
                            <label className="small">Variant ID</label>
                            <Input
                                placeholder="Variant ID"
                                value={variantId}
                                onChange={e => setVariantId(e.target.value)}
                            />
                        </Col>

                        <Col md={3}>
                            <label className="small">Type</label>
                            <Select
                                options={typeOptions}
                                value={type}
                                onChange={setType}
                                placeholder="IN / OUT"
                                isClearable
                            />
                        </Col>

                        <Col md={2}>
                            {/* <label className="small">From</label> */}
                            <DatePicker
                                selected={fromDate}
                                onChange={setFromDate}
                                className="form-control"
                                placeholderText="From date"
                                dateFormat="dd/MM/yyyy"
                                maxDate={new Date()}
                            />
                        </Col>

                        <Col md={2}>
                            {/* <label className="small">To</label> */}
                            <DatePicker
                                selected={toDate}
                                onChange={setToDate}
                                className="form-control"
                                placeholderText="To date"
                                dateFormat="dd/MM/yyyy"
                                minDate={fromDate}
                                maxDate={new Date()}
                            />
                        </Col>

                    </Row>
                </CardBody>
            </Card>

            {/* SUMMARY */}
            {summary && (
                <Row className="mb-3">
                    <Col md={4}>
                        <Card>
                            <CardBody className="d-flex gap-3 align-items-center">
                                <span className="products-in text-white">{summary.total_in}</span>
                                <div className="fw-bold fs-5">Total IN</div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card>
                            <CardBody className="d-flex gap-3 align-items-center">
                                <span className="products-out text-white">{summary.total_out}</span>
                                <div className="fw-bold fs-5">Total OUT</div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card>
                            <CardBody className="d-flex gap-3 align-items-center">
                                <span className="products-balance text-white">{summary.balance}</span>
                                <div className="fw-bold fs-5">Balance</div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* TABLE */}
            <Card>
                <CardBody>
                    <Table responsive className="modern-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Product</th>
                                <th>Variant</th>
                                <th>Type</th>
                                <th className="text-end">Qty</th>
                                <th>Note</th>
                                <th>Date</th>
                            </tr>
                        </thead>

                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="text-center text-muted">
                                        No records found
                                    </td>
                                </tr>
                            )}

                            {rows && rows.map((r, i) => (
                                <tr key={r.id}>
                                    <td>{(page - 1) * limit + i + 1}</td>
                                    <td><b>{r.product_name}</b></td>
                                    <td>{r.variant_name || <span className="text-muted">—</span>}</td>
                                    <td>
                                        <Badge color={r.change_type === "IN" ? "danger" : "success"}>
                                            {r.change_type}
                                        </Badge>
                                    </td>
                                    <td className="text-end">{qtyBadge(Math.floor(r.quantity_change))}</td>
                                    <td>{r.note || "—"}</td>
                                    <td className="small">
                                        {formatIST(r?.created_at)}
                                        {/* {new Date(r.created_at).toLocaleDateString("en-IN", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit"
                                        })} */}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <Pagination
                        page={page}
                        total={total}
                        limit={limit}
                        onPrev={() => setPage(p => Math.max(1, p - 1))}
                        onNext={() => page * limit < total && setPage(p => p + 1)}
                    />

                </CardBody>
            </Card>
        </>
    );
};

export default StockHistory;
